import { api } from '@udm/contracts';

/** Typed REST client. Every response is validated at the boundary (PROJECT_SPEC §74). */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly params?: Record<string, unknown>,
  ) {
    super(code);
  }
}

export async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  parse?: (v: unknown) => T,
  contentType = 'application/json',
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: { accept: 'application/json', ...(body !== undefined ? { 'content-type': contentType } : {}) },
      body: body === undefined ? undefined : body instanceof Blob ? body : JSON.stringify(body),
    });
  } catch {
    throw new ApiRequestError(0, 'NETWORK');
  }
  if (res.status === 204) return undefined as T;
  const json: unknown = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = api.ApiError.safeParse(json);
    throw new ApiRequestError(
      res.status,
      err.success ? err.data.code : 'INTERNAL_ERROR',
      err.success ? err.data.params : undefined,
    );
  }
  return parse ? parse(json) : (json as T);
}

export const fetchInfo = () => request('GET', '/api/v1/info', undefined, (v) => api.InfoResponse.parse(v));
export const fetchHealth = () =>
  request('GET', '/api/health/ready', undefined, (v) => api.HealthResponse.parse(v));

export interface Me {
  id: string;
  username: string;
  display_name: string;
  locale: 'en' | 'ro';
  theme: 'light' | 'dark';
  /** navigation rail pinned expanded (default) or collapsed to icons */
  nav_pinned: boolean;
  /** date/time layout in the UI; 'auto' = Intl for the UI language (absent on older backends) */
  date_format?: 'auto' | 'dd-mm-yyyy' | 'yyyy-mm-dd';
  grants: { permission: string; site_id: string | null }[];
}

/** Returns null when not signed in (401). */
export async function fetchMe(): Promise<Me | null> {
  try {
    return await request<Me>('GET', '/api/v1/auth/me');
  } catch (e) {
    if (e instanceof ApiRequestError && e.status === 401) return null;
    throw e;
  }
}
export const login = (username: string, password: string) =>
  request<Me>('POST', '/api/v1/auth/login', { username, password });
export const logout = () => request<void>('POST', '/api/v1/auth/logout');
export const updatePreferences = (p: Partial<Pick<Me, 'locale' | 'theme' | 'nav_pinned' | 'date_format'>>) =>
  request<void>('PATCH', '/api/v1/me/preferences', p);

export interface Branding {
  product_name: string;
  marketing: Partial<Record<'en' | 'ro', { title: string; subtitle: string }>>;
  logos: { light: string | null; dark: string | null; mark: string | null };
  updated_at: string | null;
}
export const fetchBranding = () => request<Branding>('GET', '/api/v1/branding');
export const saveBranding = (b: Pick<Branding, 'product_name' | 'marketing'>) =>
  request<void>('PUT', '/api/v1/admin/branding', b);
export const uploadLogo = (variant: 'light' | 'dark' | 'mark', file: File) =>
  request<{ id: string; url: string }>(
    'PUT',
    `/api/v1/admin/branding/logos/${variant}`,
    file,
    undefined,
    file.type || 'application/octet-stream',
  );
export const removeLogo = (variant: 'light' | 'dark' | 'mark') =>
  request<void>('DELETE', `/api/v1/admin/branding/logos/${variant}`);

export const can = (me: Me | null | undefined, permission: string) =>
  Boolean(me?.grants.some((g) => g.permission === permission && g.site_id === null));

/** Client account (U13): every grant is scoped to a client — no global role. */
export const isClientUser = (me: Me | null | undefined) =>
  Boolean(me && me.grants.length > 0 && me.grants.every((g) => g.site_id !== null));

/** The platform superadmin, as Central decides it (isSuperadmin): device.provision and site.manage held globally. */
export const isSuperadmin = (me: Me | null | undefined) =>
  ['device.provision', 'site.manage'].every((perm) =>
    Boolean(me?.grants.some((g) => g.permission === perm && g.site_id === null)),
  );

/** Permission somewhere (global or on any site) — decides whether a section is shown at all. */
export const canAny = (me: Me | null | undefined, permission: string) =>
  Boolean(me?.grants.some((g) => g.permission === permission));

// ---- sites (clients → localities → zones), users and roles. Server filters everything by scope. ----
export const fetchSites = () => request('GET', '/api/v1/sites', undefined, (v) => api.SiteList.parse(v));
export const createSite = (b: api.SiteCreate) =>
  request('POST', '/api/v1/sites', b, (v) => api.Site.parse(v));
/** A client / locality for a proposed commune, created atomically with its exact area and adoption. */
export const createSiteFromProposal = (b: api.SiteFromProposal) =>
  request('POST', '/api/v1/sites/from-proposal', b, (v) => api.SiteFromProposalResult.parse(v));
export const updateSite = (id: string, b: api.SiteUpdate) =>
  request('PATCH', `/api/v1/sites/${id}`, b, (v) => api.Site.parse(v));
export const deleteSite = (id: string) => request<void>('DELETE', `/api/v1/sites/${id}`);

export const fetchRoles = () => request('GET', '/api/v1/roles', undefined, (v) => api.RoleList.parse(v));
export const fetchUsers = () => request('GET', '/api/v1/users', undefined, (v) => api.UserList.parse(v));
export const createUser = (b: api.UserCreate) =>
  request('POST', '/api/v1/users', b, (v) => api.User.parse(v));
export const updateUser = (id: string, b: api.UserUpdate) =>
  request('PATCH', `/api/v1/users/${id}`, b, (v) => api.User.parse(v));
export const deleteUser = (id: string) => request<void>('DELETE', `/api/v1/users/${id}`);

// ---- mobile PWA pairing (U11) ----
export const issuePairingPin = (userId: string, resetDevice = false) =>
  request('POST', `/api/v1/users/${userId}/mobile/pin`, { reset_device: resetDevice }, (v) =>
    api.PairingPin.parse(v),
  );
export const resetMobileDevice = (userId: string) =>
  request<void>('DELETE', `/api/v1/users/${userId}/mobile/device`);

// ---- site areas and auto-adoption (U12) ----
export const fetchSiteAreas = () =>
  request('GET', '/api/v1/sites/areas', undefined, (v) => api.SiteAreas.parse(v));
export const saveSiteArea = (id: string, geometry: api.AreaGeometry | null) =>
  request<{ geometry: api.AreaGeometry | null }>('PUT', `/api/v1/sites/${id}/area`, { geometry });
/** Overlaps of a drawn area with the areas of the site's siblings (a save with any is refused: AREA_OVERLAP). */
export const checkSiteArea = (id: string, geometry: api.AreaGeometry | null) =>
  request('POST', `/api/v1/sites/${id}/area/check`, { geometry }, (v) => api.SiteAreaCheck.parse(v));
/** The drawn area pulled back to the border of the given sibling areas (null = nothing left). */
export const matchAreaBorder = (id: string, geometry: api.AreaGeometry, siteIds: string[]) =>
  request<{ geometry: api.AreaGeometry | null }>('POST', `/api/v1/sites/${id}/area/match-border`, {
    geometry,
    site_ids: siteIds,
  });
export const normalizeArea = (geometry: api.AreaGeometry) =>
  request<{ geometry: api.AreaGeometry | null }>('POST', '/api/v1/sites/areas/normalize', { geometry });
/** Site boundary suggestions from OpenStreetMap (commune limits): by name, or the commune at a point. */
export const searchAdminAreas = (q: string) =>
  request('GET', `/api/v1/admin-areas/search?q=${encodeURIComponent(q)}`, undefined, (v) =>
    api.AdminAreaSearch.parse(v),
  );
export const adminAreaAt = (lon: number, lat: number) =>
  request('GET', `/api/v1/admin-areas/at?lon=${lon}&lat=${lat}`, undefined, (v) => api.AdminAreaAt.parse(v));
/** Devices whose position does not match their site's area (after an area change), and the confirmed move. */
export const fetchPlacementReview = (siteId: string | null) =>
  request('GET', `/api/v1/sites/placement-review${siteId ? `?site_id=${siteId}` : ''}`, undefined, (v) =>
    api.PlacementReview.parse(v),
  );
/** Without siteId: to the target each position resolves to; with siteId (superadmin): to that site. */
export const applyPlacement = (deviceIds: string[], siteId?: string) =>
  request(
    'POST',
    '/api/v1/sites/placement-review/apply',
    { device_ids: deviceIds, ...(siteId ? { site_id: siteId } : {}) },
    (v) => api.PlacementApplyResult.parse(v),
  );
/** Superadmin: back to Discovered (no site). */
export const unassignPlacement = (deviceIds: string[]) =>
  request('POST', '/api/v1/sites/placement-review/unassign', { device_ids: deviceIds }, (v) =>
    api.PlacementUnassignResult.parse(v),
  );
/** GPS position correction and release (U19, superadmin): the readings, accepting them, releasing the device. */
export const fetchPositionCorrection = (deviceId: string) =>
  request('GET', `/api/v1/devices/${deviceId}/position-correction`, undefined, (v) =>
    api.PositionCorrection.parse(v),
  );
export const acceptPositionCorrection = (deviceId: string, checkId: string) =>
  request('POST', `/api/v1/devices/${deviceId}/position-correction/accept`, { check_id: checkId }, (v) =>
    api.PositionCorrectionAccepted.parse(v),
  );
export const releaseDevice = (deviceId: string, body: api.DeviceRelease) =>
  request('POST', `/api/v1/devices/${deviceId}/release`, body, (v) => api.DeviceReleased.parse(v));
export const adoptionPreview = (lon: number, lat: number) =>
  request('GET', `/api/v1/sites/adoption-preview?lon=${lon}&lat=${lat}`, undefined, (v) =>
    api.AdoptionPreview.parse(v),
  );

// ---- device discovery / adoption (U12) ----
export const fetchDiscovered = () =>
  request('GET', '/api/v1/devices/discovered', undefined, (v) => api.DiscoveredList.parse(v));
export const fetchAdoptionProposals = () =>
  request('GET', '/api/v1/adoption/proposals', undefined, (v) => api.AdoptionProposals.parse(v));
export const fetchDeviceTypes = () =>
  request('GET', '/api/v1/device-types', undefined, (v) => api.DeviceTypeList.parse(v));
export const adoptDevice = (id: string, b: api.DeviceAdopt) =>
  request('POST', `/api/v1/devices/${id}/adopt`, b, (v) => api.DeviceRecord.parse(v));
export const locateDevice = (id: string) => request<void>('POST', `/api/v1/devices/${id}/locate`);
export const createDevice = (b: api.DeviceCreate) =>
  request('POST', '/api/v1/devices', b, (v) => api.DeviceRecord.parse(v));

// ---- user manuals (docs/manuals, superadmin only) ----
export const fetchManuals = () =>
  request('GET', '/api/v1/manuals', undefined, (v) => api.ManualList.parse(v));
export const manualUrl = (lang: string, slug: string, format: 'html' | 'pdf' | 'docx', download = false) =>
  `/api/v1/manuals/${lang}/${slug}.${format}${download ? '?download' : ''}`;
